import { useEffect, useRef, useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { V4 } from '../tokens';
import type { FeatureVariant } from '@utils/featureRollout/types';

// Card reutilizable del framework de rollout (generaliza el molde VersionPickerCard).
// Dos modos según la variante activa:
//   - control   → invitación "Prueba la nueva versión" (acción: probar treatment).
//   - treatment → "Regresar a la versión anterior" (acción: revert a control), si can_revert.
//
// El switch lo dispara SOLO el root. Para no-root mostramos un aviso suave (no rompemos):
// el backend respondería REQUIRES_ROOT, así que ni siquiera optimizamos el switch local.
//
// V4: zero Tailwind, 100% inline styles con tokens.

export interface FeatureVersionCardProps {
  variant: FeatureVariant;
  canRevert: boolean;
  gaForced: boolean;
  isRoot: boolean;
  // Dispara el switch (fire-and-forget). El consumidor llama switchTo/revert del hook.
  onTry: () => void;
  onRevert: () => void;
  // Se llama una vez cuando la invitación es visible (telemetría invitation_shown).
  onShown?: () => void;
  newLabel?: string;
  newDescription?: string;
}

const SparkIcon: ReactNode = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
  </svg>
);

const BackIcon: ReactNode = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M11 5l-7 7 7 7M4 12h16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon: ReactNode = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    aria-hidden="true"
  >
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const cardStyle: CSSProperties = {
  background: V4.white,
  borderRadius: 10,
  border: `1px solid ${V4.grayLight}`,
  overflow: 'hidden',
  fontFamily: 'inherit',
};

const primaryButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: V4.secondary,
  color: V4.white,
  border: 0,
  borderRadius: 8,
  padding: '9px 14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
};

// Treatment: pill discreto para "volver" (no un botón de banner). Slim, redondeado.
const revertPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: V4.white,
  color: V4.secondaryDark,
  border: `1px solid ${V4.secondaryLight2}`,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 700,
};

// Etiqueta compacta de estado "estás en la versión nueva": acento success para que se
// note (no pasa desapercibida) sin ocupar un banner que rompa el layout de V2.
const newTag: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  background: V4.successFaded,
  color: V4.successDark,
  borderRadius: 999,
  padding: '5px 10px',
  fontFamily: 'inherit',
  fontSize: 11.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

export const FeatureVersionCard: FC<FeatureVersionCardProps> = ({
  variant,
  canRevert,
  gaForced,
  isRoot,
  onTry,
  onRevert,
  onShown,
  newLabel = 'Prueba la nueva versión',
  newDescription = 'Rediseñamos esta sección. Pruébala cuando quieras y regresa a la anterior si lo prefieres.',
}) => {
  const [softNotice, setSoftNotice] = useState<string | null>(null);
  const shownRef = useRef(false);

  // Telemetría invitation_shown: una vez al montar en estado invitación/regreso.
  // Usamos un ref como guard para que el efecto pueda depender de onShown sin re-disparar.
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    onShown?.();
  }, [onShown]);

  // Si la feature graduó a GA (ga_forced) ya no se ofrece regresar: no renderizamos card.
  if (gaForced) return null;

  const guardRoot = (action: () => void) => {
    if (!isRoot) {
      setSoftNotice('Solo el administrador principal (root) puede cambiar la versión de este comercio.');
      return;
    }
    setSoftNotice(null);
    action();
  };

  const isTreatment = variant === 'treatment';

  // En treatment solo mostramos "regresar" si el backend lo permite (can_revert).
  if (isTreatment && !canRevert) return null;

  // Treatment: barra DISCRETA y descubrible, alineada a la derecha. No es un banner que
  // empuje/rompa el layout de V2; es solo el acceso a "volver" + una etiqueta de estado
  // con acento success para que se note. El anuncio grande se reserva para la invitación.
  if (isTreatment) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <span style={newTag}>
            {CheckIcon}
            Estás en la versión nueva
          </span>
          <button type="button" style={revertPill} onClick={() => guardRoot(onRevert)}>
            {BackIcon}
            Volver a la anterior
          </button>
        </div>
        {softNotice && (
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              borderRadius: 8,
              background: V4.warningFaded,
              color: V4.warningDark,
              fontSize: 12,
              fontWeight: 600,
              textAlign: 'right',
            }}
          >
            {softNotice}
          </div>
        )}
      </div>
    );
  }

  // Control: invitación a probar la nueva versión. Aquí SÍ es una card con presencia
  // (es una oferta y debe destacar para que el comercio la descubra y acepte).
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: V4.grayBlueLight,
            color: V4.secondary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {SparkIcon}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: V4.primary }}>{newLabel}</span>
          <span style={{ display: 'block', fontSize: 11.5, color: V4.grayUltraDark, marginTop: 2, lineHeight: 1.4 }}>
            {newDescription}
          </span>
        </span>
        <button type="button" style={primaryButton} onClick={() => guardRoot(onTry)}>
          {SparkIcon}
          Probar ahora
        </button>
      </div>
      {softNotice && (
        <div
          style={{
            padding: '8px 14px',
            background: V4.warningFaded,
            color: V4.warningDark,
            fontSize: 12,
            fontWeight: 600,
            borderTop: `1px solid ${V4.grayLight}`,
          }}
        >
          {softNotice}
        </div>
      )}
    </div>
  );
};

export default FeatureVersionCard;
